"use strict";
/**
 * Merchant subdomain rules — the single source of truth for the whole platform.
 *
 * A merchant's subdomain is not just a DNS label: the provisioner derives Kubernetes
 * object names from it (`<subdomain>-backend`, `-admin`, `-storefront`, `-config`,
 * `-secrets`, `-ingress`). Kubernetes **Service** names must be DNS-1035 labels, which
 * — unlike DNS-1123, used for Pods/Deployments/ConfigMaps — must START WITH A LETTER.
 *
 * That one-character difference caused BUG-MREG-SUBDOMAIN-DNS1035: registration
 * accepted the subdomain `123`, the merchant was approved (`status='active'`), and only
 * then did provisioning die with
 *   Service "123-backend" is invalid: metadata.name: Invalid value: "123-backend":
 *   a DNS-1035 label must ... start with an alphabetic character
 * leaving orphaned Deployments and a merchant that could never have a storefront.
 *
 * Keep this module dependency-free: it is imported by core-backend (Node) and by
 * core-frontend (browser bundle) so both enforce exactly the same rule.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SUBDOMAIN_VIOLATION_MESSAGES = exports.SUBDOMAIN_LONGEST_SUFFIX = exports.SUBDOMAIN_MAX_LENGTH = exports.SUBDOMAIN_MIN_LENGTH = exports.SUBDOMAIN_PATTERN = void 0;
exports.isReservedSubdomain = isReservedSubdomain;
exports.checkSubdomain = checkSubdomain;
exports.isValidSubdomain = isValidSubdomain;
exports.describeSubdomainViolation = describeSubdomainViolation;
exports.toSubdomain = toSubdomain;
/** DNS-1035 label: starts with a letter, then letters/digits/hyphens, ends alphanumeric. */
exports.SUBDOMAIN_PATTERN = /^[a-z]([-a-z0-9]*[a-z0-9])?$/;
exports.SUBDOMAIN_MIN_LENGTH = 3;
/**
 * 52, not 63. A DNS label may be 63 characters, but the longest name the provisioner
 * builds from the subdomain is `<subdomain>-storefront` (11 characters of suffix), and
 * that name must itself fit in 63. Allowing 63 here would reproduce the same class of
 * bug the pattern above fixes: accepted at registration, fatal at provisioning time.
 */
exports.SUBDOMAIN_MAX_LENGTH = 52;
/** Longest suffix appended by K8sResourceProvisioner — keeps the 52 above honest. */
exports.SUBDOMAIN_LONGEST_SUFFIX = '-storefront';
/**
 * `dev` and `dev-*` belong to the dev environment: the auth layer maps
 * `dev.teletienda.ar` and every `dev-*.teletienda.ar` to dev and keeps its session
 * in separate cookies (BUG-SSO-SESSION-AUTHORITY). A prod merchant on such a
 * subdomain would land in the dev session space.
 */
function isReservedSubdomain(subdomain) {
    const lower = subdomain.toLowerCase();
    return lower === 'dev' || lower.startsWith('dev-');
}
/**
 * Return the specific rule a subdomain breaks, or null when it is valid.
 * The order matters: the most explanatory violation wins, so a user typing `123`
 * is told "must start with a letter" rather than a generic "invalid characters".
 */
function checkSubdomain(subdomain) {
    if (typeof subdomain !== 'string' || subdomain.length === 0)
        return 'empty';
    if (isReservedSubdomain(subdomain))
        return 'reserved_prefix';
    if (subdomain.length < exports.SUBDOMAIN_MIN_LENGTH)
        return 'too_short';
    if (subdomain.length > exports.SUBDOMAIN_MAX_LENGTH)
        return 'too_long';
    if (exports.SUBDOMAIN_PATTERN.test(subdomain))
        return null;
    if (!/^[a-z]/.test(subdomain))
        return 'must_start_with_letter';
    if (!/[a-z0-9]$/.test(subdomain))
        return 'must_end_with_alphanumeric';
    return 'invalid_characters';
}
function isValidSubdomain(subdomain) {
    return checkSubdomain(subdomain) === null;
}
/** English messages for server-side errors. UIs localize `SubdomainViolation` instead. */
exports.SUBDOMAIN_VIOLATION_MESSAGES = {
    empty: 'Subdomain is required',
    reserved_prefix: 'Subdomains "dev" and "dev-…" are reserved for the development environment',
    too_short: `Subdomain must be at least ${exports.SUBDOMAIN_MIN_LENGTH} characters`,
    too_long: `Subdomain must be at most ${exports.SUBDOMAIN_MAX_LENGTH} characters`,
    must_start_with_letter: 'Subdomain must start with a letter (a-z)',
    must_end_with_alphanumeric: 'Subdomain must end with a letter or digit',
    invalid_characters: 'Subdomain may contain only lowercase letters, digits and hyphens',
};
function describeSubdomainViolation(violation) {
    return exports.SUBDOMAIN_VIOLATION_MESSAGES[violation];
}
/**
 * Derive a valid subdomain from free text (a business name, usually).
 *
 * Returns an empty string when nothing usable survives — callers decide the fallback,
 * because a random one is right for the server and wrong for a form field the user is
 * still typing into. Leading digits are dropped rather than prefixed: "123 Store"
 * becomes "store", which is what a person would have written by hand.
 *
 * A reserved result ("Dev Shop" → `dev-shop`) also yields an empty string rather than
 * a silently altered name the user never typed.
 */
function toSubdomain(input) {
    if (typeof input !== 'string')
        return '';
    const slug = input
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // strip diacritics
        .replace(/[^a-z0-9]+/g, '-') // any run of other characters becomes one hyphen
        .replace(/^[^a-z]+/, '') // DNS-1035: must start with a letter
        .slice(0, exports.SUBDOMAIN_MAX_LENGTH)
        .replace(/[^a-z0-9]+$/, ''); // must end alphanumeric (also trims a sliced hyphen)
    if (slug.length < exports.SUBDOMAIN_MIN_LENGTH || isReservedSubdomain(slug))
        return '';
    return slug;
}
